import { forwardRef } from 'react';

import { cn } from '../../../lib/utils';

interface ColumnFilterTriggerProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;
}

/**
 * Purpose-built trigger button for column filter dropdowns.
 *
 * Designed to feel like a table header (subtle, anchored) rather than
 * a floating button. Maintains accessibility with focus-visible ring.
 *
 * Use with DropdownButton: `<DropdownButton as={ColumnFilterTrigger}>`
 */
export const ColumnFilterTrigger = forwardRef<
  HTMLButtonElement,
  ColumnFilterTriggerProps
>(({ className, children, ...props }, ref) => (
  <button
    ref={ref}
    type="button"
    className={cn(
      // Layout
      'inline-flex items-center',
      // Spacing - balanced: compact but breathable
      // Negative margin offsets padding to maintain text alignment with cell values
      'rounded px-2 py-0.5 -ml-2',
      // Interactive states - subtle, table-header appropriate
      'hover:bg-zinc-100 dark:hover:bg-zinc-800',
      'transition-colors',
      // Focus - accessible keyboard navigation
      'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1',
      // Cursor
      'cursor-pointer',
      className,
    )}
    {...props}
  >
    {children}
  </button>
));

ColumnFilterTrigger.displayName = 'ColumnFilterTrigger';
